import { Role } from './constants';
import { useOrganization } from '@/contexts/organization-context';
import { Navigate } from 'react-router-dom';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: Role[];
  excludedRoles?: Role[];
}

/**
 * RoleGuard with support for both allowed and excluded roles.
 * Backend handles role authorization for API calls.
 */
export const RoleGuard = ({
  children,
  allowedRoles,
  excludedRoles,
}: RoleGuardProps) => {
  const { currentInstitution, currentOrganization } = useOrganization();
  const userRoles = currentInstitution?.roles || currentOrganization?.roles || [];
  
  // Check excluded roles first
  if (excludedRoles && excludedRoles.length > 0) {
    const hasExcludedRole = excludedRoles.some(role => userRoles.includes(role));
    
    if (hasExcludedRole) {
      // Redirect to unauthorized page
      return <Navigate to="/401" replace />;
    }
  }
  
  // If specific roles are required, check them
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => userRoles.includes(role));
    
    if (!hasRequiredRole) {
      // Redirect to unauthorized page
      return <Navigate to="/401" replace />;
    }
  }
  
  // Since backend is now the single source of truth for roles and permissions,
  // we simply render the children. Backend API calls will handle authorization.
  return <>{children}</>;
}; 